import { DataGrid } from '@mui/x-data-grid';
import { groupBy, get } from 'lodash';
import { v7 as uuidv7 } from 'uuid';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

// Commons
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { Dropdown } from '@commons/utils/styledLibraryComponents';
import { formatNumber } from '@commons/DisplayNumber';
import { generateUUID } from '@commons/utils/uuid';
import { getFactorToConvertMetricToEntityUnit } from '@commons/utils/conversion';
import { REGEX_IMAGE_FILE_EXTENSION } from '@commons/utils/regex';
import Text, { ENUM_FONTS } from '@commons/Text';

// Styled & components
import {
  DataGridContainer,
  DataGridHeader,
  DataGridHeaderTextContainer,
} from '../styledComponents';
import { GridContainer } from './styledComponents';
import EmptyState from '@stocks/StocksInventories/StocksInventoriesTransfer/components/TransferForm/components/EmptyState';

// Miscellaneous
import { getRecipesColumns } from './getColumns';
import utils from '@admin/components/HeaderPicture/utils/files';

const renderEmptyState = (selectedDate, selectedStore) => (
  <>
    <EmptyState
      icon={'/images/inpulse/product.svg'}
      subtitle={i18next.t('LOSSES.PRODUCTS.NO_LOSSES_SUBTITLE', {
        date: selectedDate.format(DATE_DISPLAY_FORMATS.DATE_MONTH),
        storeName: selectedStore.name,
      })}
      title={i18next.t('LOSSES.PRODUCTS.NO_LOSSES_TITLE')}
    />
  </>
);

const RecipesGrid = (props) => {
  const {
    recipesDropdownItems,
    isLoading,
    recipesTotal,
    recipeRows,
    currency,
    setRecipeRows,
    hasMultipleChannels,
    channels,
    lossCategories,
    setRecipesDropdownItems,
    setRecipesTotal,
    setIsDirty,
    isCentralMode,
    canCreateOrUpdateLosses,
    canCorrectLosses,
    selectedDate,
    selectedStore,
    setShouldPictureFullScreen,
  } = props;

  const [columns, setColumns] = useState([]);

  useEffect(() => {
    setColumns(
      getRecipesColumns({
        lossCategories,
        currency,
        handleFieldUpdate,
        handleItemRemoval,
        recipeRows,
        hasMultipleChannels,
        isCentralMode,
        canCreateOrUpdateLosses,
        canCorrectLosses,
        handleReplacePicture,
        handleDeletePicture,
        setShouldPictureFullScreen,
      }),
    );

    const recipesTotal = recipeRows.reduce((acc, row) => {
      const costByChannelId = row.costByChannelId[row.channelId];

      const quantity = row.quantity * getFactorToConvertMetricToEntityUnit(row.unit);

      const total =
        costByChannelId && hasMultipleChannels ? costByChannelId * quantity : row.cost * quantity;

      return acc + total;
    }, 0);

    setRecipesTotal(recipesTotal);
  }, [
    lossCategories,
    recipeRows,
    channels,
    hasMultipleChannels,
    recipesDropdownItems,
    canCorrectLosses,
    canCreateOrUpdateLosses,
  ]);

  /**
   * Handles adding a new loss for a client that does not have multiple channels enabled
   *
   * @param {Object} formattedItem - The formatted recipe loss item to be added to the grid
   * @param {Object} item - The original recipe item selected from the dropdown
   * @param {Array} associatedLosses - Array of existing losses already associated with this recipe
   *
   * The function:
   * 1. Gets all loss category IDs that are already used for this recipe
   * 2. Assigns the first available loss category to the new loss item
   * 3. Updates the recipe rows state by adding the new loss
   * 4. Updates the recipes dropdown by disabling the recipe if all possible loss categories are used
   */
  const handleNewLossForClientWithoutMultipleChannels = (formattedItem, item, associatedLosses) => {
    const associatedLossesCategoryIds = new Set(
      associatedLosses.map(({ lossCategoryId }) => lossCategoryId),
    );

    lossCategories.forEach((lossCategory) => {
      if (formattedItem.lossCategoryId !== undefined) {
        return;
      }

      if (associatedLossesCategoryIds.has(lossCategory.id)) {
        return;
      }

      formattedItem.lossCategoryId = lossCategory.id;
    });

    setRecipeRows([formattedItem, ...recipeRows]);

    const updatedRecipesDropdownItems = recipesDropdownItems.map((recipe) => {
      if (item.recipeId === recipe.recipeId) {
        return {
          ...item,
          // +1 to take current loss into account
          isDisabled: lossCategories.length === associatedLosses.length + 1,
        };
      }

      return recipe;
    });

    setRecipesDropdownItems(updatedRecipesDropdownItems);
  };

  /**
   * Assigns a channel and loss category to a recipe for clients with multiple channels enabled
   *
   * @param {Object} recipe - The recipe object to be updated with channel and loss category
   * @param {Array<string>} allAssociatedLossesCompositeKeys - Array of existing composite keys in format "channelId-lossCategoryId"
   * @returns {Object} The updated recipe with assigned channelId, lossCategoryId and composite key
   *
   * The function:
   * 1. Finds the first available channel that hasn't used all its loss categories
   * 2. Assigns the first available loss category that isn't used for the selected channel
   * 3. Creates and assigns a composite key using channelId-lossCategoryId format
   */
  const updateRecipeForClientWithMultipleChannels = (recipe, allAssociatedLossesCompositeKeys) => {
    const updatedRecipe = { ...recipe };

    updatedRecipe.channels.forEach((channel) => {
      if (updatedRecipe.channelId) {
        return;
      }

      const channelId = get(channel, 'id', null);

      const compositeKeysForChannel = allAssociatedLossesCompositeKeys.filter((key) =>
        key.includes(channelId),
      );

      if (compositeKeysForChannel.length === lossCategories.length) {
        return;
      }

      updatedRecipe.channelId = channelId;
    });

    lossCategories.forEach((lossCategory) => {
      if (updatedRecipe.lossCategoryId !== undefined) {
        return;
      }

      if (
        allAssociatedLossesCompositeKeys.includes(`${updatedRecipe.channelId}-${lossCategory.id}`)
      ) {
        return;
      }

      updatedRecipe.lossCategoryId = lossCategory.id;
    });

    updatedRecipe.key = `${updatedRecipe.channelId}-${updatedRecipe.lossCategoryId}`;

    return updatedRecipe;
  };

  /**
   * Handles adding a new loss for a client with multiple activated channels
   *
   * @param {Object} formattedItem - The formatted recipe loss item to be added
   * @param {Object} item - The original recipe item selected from the dropdown
   * @param {Array} associatedLosses - Array of existing losses already associated with this recipe
   *
   * The function:
   * 1. First extracts all existing composite keys (channelId-lossCategoryId) from associated losses
   * 2. Assigns a channel to formattedItem by:
   *    - Finding the first available channel that hasn't used all its loss categories
   *    - A channel is "full" when it has losses for all possible categories
   * 3. Assigns a loss category to formattedItem by:
   *    - Finding the first available category that isn't already used for the selected channel
   * 4. Creates a composite key for the new loss using channelId-lossCategoryId
   * 5. Updates the recipe rows state and dropdown items
   */
  const handleNewLossForClientWithMultipleChannels = (formattedItem, item, associatedLosses) => {
    const allAssociatedLossesCompositeKeys = associatedLosses.reduce((acc, { key }) => {
      if (!!key) {
        acc.push(key);
      }

      return acc;
    }, []);

    const updatedRecipe = updateRecipeForClientWithMultipleChannels(
      formattedItem,
      allAssociatedLossesCompositeKeys,
    );

    setRecipeRows([updatedRecipe, ...recipeRows]);

    const allPossibleKeysForRecipe = updatedRecipe.channels.reduce((acc, channel) => {
      // Check that the channel exists and has an id
      if (!channel || !channel.id) {
        return acc;
      }

      lossCategories.forEach((lossCategory) => {
        acc.push(`${channel.id}-${lossCategory.id}`);
      });

      return acc;
    }, []);

    const updatedRecipesDropdownItems = recipesDropdownItems.map((recipe) => {
      if (item.recipeId === recipe.recipeId) {
        return {
          ...item,
          // +1 to take current loss into account
          isDisabled:
            allPossibleKeysForRecipe.length === allAssociatedLossesCompositeKeys.length + 1,
        };
      }

      return recipe;
    });

    setRecipesDropdownItems(updatedRecipesDropdownItems);
  };

  const addNewLoss = (item) => {
    const formattedItem = {
      ...item,
      lossPictureUrl: null,
      lossPictureImage: null,
      // We want to keep the default channelId if client doesn't have multipleChannels
      channelId: hasMultipleChannels ? null : item.channelId,
      lossCategoryId: undefined, // Undefined instead of null to keep the difference between no category and category none
      isCashierLoss: false,
      id: generateUUID(),
    };

    setIsDirty(true);

    const recipeRowGroupedByRecipeIds = groupBy(recipeRows, 'recipeId');
    const associatedLosses = recipeRowGroupedByRecipeIds[item.recipeId] || [];

    if (!hasMultipleChannels) {
      handleNewLossForClientWithoutMultipleChannels(formattedItem, item, associatedLosses);
      return;
    }

    handleNewLossForClientWithMultipleChannels(formattedItem, item, associatedLosses);
  };

  const handleFieldUpdate = (field, value, lossId) => {
    setIsDirty(true);

    const updatedRecipeRows = recipeRows.map((row) => {
      if (row.id === lossId) {
        row[field] = value;

        // When changing the channel of a loss, we need to:
        // 1. Check if the combination of new channel + current loss category already exists
        // 2. If it doesn't exist, we can use this combination as the new key
        // 3. If it exists, we get loss category that is not used for the new channel and generate a new valid key
        if (field === 'channelId') {
          const recipeRowGroupedByRecipeIds = groupBy(recipeRows, 'recipeId');
          const associatedLosses = recipeRowGroupedByRecipeIds[row.recipeId] || [];

          const allAssociatedLossesCompositeKeys = associatedLosses.reduce((acc, { key }) => {
            if (!!key) {
              acc.push(key);
            }

            return acc;
          }, []);

          const potentialKey = `${value}-${row.lossCategoryId}`;

          if (!allAssociatedLossesCompositeKeys.includes(potentialKey)) {
            row.key = potentialKey;
            return row;
          }

          // We set the loss category to undefined to keep the difference between no category and category none
          row.lossCategoryId = undefined;

          row = updateRecipeForClientWithMultipleChannels(row, allAssociatedLossesCompositeKeys);
        }

        if (field === 'lossCategoryId') {
          row.key = `${row.channelId}-${value}`;
        }
      }

      return row;
    });

    setRecipeRows(updatedRecipeRows);
  };

  const handleRowSelectionChange = (selectedRowIds) => {
    const selectedRowIdsSet = new Set(selectedRowIds);

    const updatedRecipeRows = recipeRows.map((row) => ({
      ...row,
      isRowSelected: selectedRowIdsSet.has(row.id),
    }));

    setRecipeRows(updatedRecipeRows);
  };

  const handleItemRemoval = (item) => {
    setIsDirty(true);

    const updatedRecipeRows = recipeRows.filter((row) => row.id !== item.id);

    setRecipeRows(updatedRecipeRows);

    const updatedRecipesDropdownItems = recipesDropdownItems.map((recipe) => {
      if (item.recipeId === recipe.recipeId) {
        return {
          ...recipe,
          isDisabled: false,
        };
      }

      return recipe;
    });

    setRecipesDropdownItems(updatedRecipesDropdownItems);
  };

  const handleReplacePicture = async (event, lossId) => {
    const file = event.target.files[0];

    if (file) {
      // We need to rename the file to avoid conflicts if multiple files with the same name are uploaded
      const newFileName = REGEX_IMAGE_FILE_EXTENSION.test(file.name)
        ? file.name.replace(REGEX_IMAGE_FILE_EXTENSION, `_${uuidv7()}.$1`)
        : `${file.name}_${uuidv7()}`;

      const renamedFile = new File([file], newFileName, { type: file.type });

      const loadedPicture = await utils.load(renamedFile);

      handleFieldUpdate('lossPictureUrl', null, lossId);
      handleFieldUpdate('lossPictureImage', loadedPicture, lossId);
    }
  };

  const handleDeletePicture = (lossId) => {
    handleFieldUpdate('lossPictureUrl', null, lossId);
    handleFieldUpdate('lossPictureImage', null, lossId);
  };

  return (
    <DataGridContainer>
      <DataGridHeader>
        <Dropdown
          iconSrc={'/images/inpulse/search-dmgrey.svg'}
          isDisabled={isLoading || !canCreateOrUpdateLosses || !canCorrectLosses}
          items={recipesDropdownItems}
          placeholder={i18next.t('LOSSES.INVENTORIES.DROPDOWN_PLACEHOLDER')}
          searchPlaceholder={i18next.t('GENERAL.SEARCH')}
          selectedItem={null}
          width={'380px'}
          isRequired
          onSelectionChange={addNewLoss}
        />
        <DataGridHeaderTextContainer>
          <Text font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {i18next.t('LOSSES.INVENTORIES.NUMBER_OF_LINES', {
              count: recipeRows.length,
            })}
          </Text>
          <Text font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
            {i18next.t('LOSSES.INVENTORIES.TOTAL_EXCL_TAXES', {
              currencyCode: currency.alphabeticCode,
              total: formatNumber(recipesTotal, currency.numberDecimals),
            })}
          </Text>
        </DataGridHeaderTextContainer>
      </DataGridHeader>
      <GridContainer>
        <DataGrid
          columns={columns}
          rows={recipeRows}
          slots={{
            noRowsOverlay: () => renderEmptyState(selectedDate, selectedStore),
          }}
          sx={{
            height: '100%',
            border: '0px',
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
              {
                outline: 'none',
              },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '14px',
            },
          }}
          autosizeOnMount
          disableColumnFilter
          disableColumnMenu
          disableColumnResize
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          hideFooter
          ignoreDiacritics
          onRowSelectionModelChange={handleRowSelectionChange}
        />
      </GridContainer>
    </DataGridContainer>
  );
};

export default RecipesGrid;
